import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export type TWWord = {
  text: string;
  className?: string;
};

type Props = {
  words: TWWord[];
  className?: string;
  cursorClassName?: string;
  /** ms per character. */
  speed?: number;
};

/**
 * Aceternity typewriter-effect-demo-1: types the phrase out
 * character-by-character. Each word can carry its own className so
 * you get accent-coloured words mixed with normal text.
 */
export function TypewriterEffect({
  words,
  className,
  cursorClassName,
  speed = 60
}: Props) {
  const phrase = React.useMemo(() => {
    // Flatten to chars, tagging each with its source word index.
    const chars: { ch: string; wordIdx: number; isSpace: boolean }[] = [];
    words.forEach((w, i) => {
      Array.from(w.text).forEach(ch => chars.push({ ch, wordIdx: i, isSpace: false }));
      if (i < words.length - 1) chars.push({ ch: " ", wordIdx: i, isSpace: true });
    });
    return chars;
  }, [words]);

  const [shown, setShown] = React.useState(0);

  React.useEffect(() => {
    setShown(0);
    if (phrase.length === 0) return;
    let i = 0;
    const t = window.setInterval(() => {
      i++;
      setShown(i);
      if (i >= phrase.length) window.clearInterval(t);
    }, speed);
    return () => window.clearInterval(t);
  }, [phrase, speed]);

  return (
    <span className={cn("inline-flex flex-wrap items-baseline gap-x-1", className)}>
      {words.map((w, wi) => {
        // Chars belonging to this word that have been revealed.
        const visible = phrase
          .filter(c => c.wordIdx === wi && !c.isSpace)
          .slice(0, phrase.slice(0, shown).filter(c => c.wordIdx === wi && !c.isSpace).length);
        const text = visible.map(c => c.ch).join("");
        if (text.length === 0) return null;
        return (
          <span key={wi} className={w.className}>{text}</span>
        );
      })}
      <motion.span
        aria-hidden
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
        className={cn("inline-block w-[3px] h-[0.9em] align-baseline bg-accent", cursorClassName)}
      />
    </span>
  );
}
